import { useRef, useCallback } from 'react';
import { usePrivy, useSessionSigners } from '@privy-io/react-auth';
import { useUserInfo, useEvmAddress, useSolanaAddress } from '@/app/stores/useUserStore';
import { api } from '@/lib/api';

/**
 * Session Signer 授权 Hook
 * 用于授权服务端代签交易
 */
export function useSessionSignerAuth() {
  const { user, ready } = usePrivy();
  const userInfo = useUserInfo();
  const evmAddress = useEvmAddress();
  const solanaAddress = useSolanaAddress();
  const { addSessionSigners } = useSessionSigners();

  // 防止重复调用
  const isAuthorizing = useRef(false);

  /**
   * 检查并自动授权
   */
  const checkAndAuthorize = useCallback(async () => {
    // 防止重复调用
    if (isAuthorizing.current) {
      console.log('Signer授权-正在授权中，跳过');
      return;
    }

    // 前置条件检查
    if (!ready || !user || !userInfo) return;
    if (!evmAddress && !solanaAddress) return;

    // 查找钱包并判断授权状态
    const evmWallet = user.linkedAccounts.find(
      (account): account is Extract<typeof account, { type: 'wallet' }> =>
        account.type === 'wallet' &&
        account.address?.toLowerCase() === evmAddress?.toLowerCase()
    );
    const solanaWallet = user.linkedAccounts.find(
      (account): account is Extract<typeof account, { type: 'wallet' }> =>
        account.type === 'wallet' &&
        account.address === solanaAddress
    );

    const evmNeedAuth = evmAddress && evmWallet && !evmWallet.delegated;
    const solanaNeedAuth = solanaAddress && solanaWallet && !solanaWallet.delegated;

    if (!evmNeedAuth && !solanaNeedAuth) {
      console.log('Signer授权-无需授权');
      return;
    }

    // 开始授权
    isAuthorizing.current = true;
    console.log('Signer授权-需要授权:', { evmNeedAuth, solanaNeedAuth });

    try {
      const response = await api.privy.getSignerId();
      const signerId = response.data?.signerId;
      if (!signerId) throw new Error('无法获取 signerId');

      if (evmNeedAuth) {
        await addSessionSigners({
          address: evmAddress!,
          signers: [{ signerId, policyIds: [] }],
        });
        console.log('Signer授权-EVM 成功');
      }

      if (solanaNeedAuth) {
        await addSessionSigners({
          address: solanaAddress!,
          signers: [{ signerId, policyIds: [] }],
        });
        console.log('Signer授权-Solana 成功');
      }
    } catch (error) {
      console.error('Signer授权-失败:', error);
    } finally {
      isAuthorizing.current = false;
    }
  }, [ready, user, userInfo, evmAddress, solanaAddress, addSessionSigners]);

  return { checkAndAuthorize };
}
